import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { bookingAPI, propertyAPI, propertySettingsAPI, moduleConfigAPI } from '../services/api';
import { 
  ArrowLeft, User, Phone, Calendar, 
  Loader2, Plus, Minus, Home, CreditCard, Banknote, CheckCircle,
  MessageCircle, CalendarPlus, Users, ClipboardList
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import { computeWalkinPricing, GST_MODES } from '../utils/walkinGst';

const BookingCreateScreen = () => {
  const navigate = useNavigate();
  const { selectedProperty } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState('');
  const [errors, setErrors] = useState({});

  // Room types — single source of truth: configured via the Onboarding Form
  const [roomTypes, setRoomTypes] = useState([]);

  // iter87 Phase C — Auto-pricing state
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  // Once the user manually edits Total Amount, auto-fill stops overwriting it
  // until a new room/date/count change resets the flag back to false.
  const [manualOverride, setManualOverride] = useState(false);

  // iter88 Phase D/E — extras + paid facilities + GST
  const [paidFacilities, setPaidFacilities] = useState([]); // [{name, price}] from onboarding
  const [selectedFacilities, setSelectedFacilities] = useState({}); // {name: qty}
  const [childrenAges, setChildrenAges] = useState([]); // [int]
  const [extraAdultCount, setExtraAdultCount] = useState(0);
  // iter170 — GST mode replaces the boolean `gstEnabled` toggle. One of:
  //   'none'      — GST=0, GSTIN hidden (default)
  //   'excluding' — GST added on top of the entered rate
  //   'including' — GST already inside the entered rate (extract from total)
  const [gstMode, setGstMode] = useState('none');
  const [propertyGstin, setPropertyGstin] = useState('');
  const [gstinLoaded, setGstinLoaded] = useState(false);
  // iter174 — Admin-controlled GST config, per property. Shape:
  //   { gst_enabled: bool, gst_percentage: number|null }
  // Fetched from /api/config/modules?property_id=... on property change.
  const [gstAdminConfig, setGstAdminConfig] = useState({ gst_enabled: true, gst_percentage: null });

  // iter189 — Admin-panel walk-in FORM config, fetched via our backend
  // proxy `/api/config/walkin-form`. Only the following optional fields
  // can be hidden / marked required from the panel; every other field
  // in this screen is structurally required for pricing + invoice and
  // is always rendered regardless of the config.
  const OPTIONAL_FORM_KEYS = useMemo(() => new Set([
    'whatsapp_number', 'no_of_children', 'advance_amount', 'gst_number',
    'extra_bed_count',
  ]), []);
  const [walkinFormConfig, setWalkinFormConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/config/walkin-form?refresh=1`;
    axios.get(url, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const fields = res?.data?.RESULT?.fields;
        setWalkinFormConfig(Array.isArray(fields) ? fields : []);
      })
      .catch(() => { if (!cancelled) setWalkinFormConfig([]); });
    return () => { cancelled = true; };
  }, []);
  // Map keyed by field key → {shown, required}. Structural fields always
  // resolve to shown=true, required=true (fail-open).
  const formFieldCfg = useMemo(() => {
    const cfg = {};
    (walkinFormConfig || []).forEach((f) => {
      if (!f || !f.key) return;
      cfg[f.key] = { shown: f.shown !== false, required: !!f.required };
    });
    return cfg;
  }, [walkinFormConfig]);
  const fieldShown = useCallback((key) => {
    if (!OPTIONAL_FORM_KEYS.has(key)) return true;   // structural — always shown
    if (!formFieldCfg[key]) return true;             // default: shown (fail-open)
    return formFieldCfg[key].shown;
  }, [OPTIONAL_FORM_KEYS, formFieldCfg]);
  const fieldRequired = useCallback((key, fallback = false) => {
    if (!formFieldCfg[key]) return fallback;
    return formFieldCfg[key].required;
  }, [formFieldCfg]);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    mobile_number: '',
    // iter169 — Each room row now carries a manually editable per-night rate.
    // Seeded from the Onboarding Form's `base_price` on room-type selection;
    // user overrides win and drive the GST slab + charges calculation.
    rooms: [{ room_name: '', room_count: 1, room_rate: '' }],
    payment_mode: 'Cash',
    // New payment fields
    advance_amount: '',
    total_amount: '',
    check_in_date: format(new Date(), 'yyyy-MM-dd'),
    check_out_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    booked_on_date: format(new Date(), 'yyyy-MM-dd'), // New required field
    // Guest count
    no_of_adults: 1,
    no_of_children: 0,
    // iter112 — Extra beds requested by guest (walk-in only)
    extra_bed_count: 0,
    // WhatsApp fields
    send_whatsapp: false,
    whatsapp_same_as_mobile: true,
    whatsapp_number: '',
    // iter278 — Send Menu PDF toggle. Independent of the voucher/same
    // toggles. Backend dispatches menu ~10s after voucher when ON AND
    // property has a menu file.
    send_menu: false,
  });

  // Load this property's configured room types for the dropdown.
  const loadRoomTypes = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await propertyAPI.getRoomTypes(selectedProperty.id);
      setRoomTypes(res?.STATUS === 1 ? (res.RESULT || []) : []);
    } catch (e) {
      setRoomTypes([]);
    }
  }, [selectedProperty]);

  // iter88 Phase D — Load paid facilities from the property's onboarding form.
  const loadFacilities = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await propertyAPI.getFacilities(selectedProperty.id);
      setPaidFacilities(res?.STATUS === 1 ? (res.RESULT || []) : []);
    } catch (e) {
      setPaidFacilities([]);
    }
  }, [selectedProperty]);

  useEffect(() => {
    loadRoomTypes();
    loadFacilities();
  }, [loadRoomTypes, loadFacilities]);

  // iter170 — Load the property-scoped GSTIN so the pricing summary + booking
  // payload can reference it when GST is applied. Reloads on property switch
  // so a stale GSTIN from a different property is never shown.
  useEffect(() => {
    let mounted = true;
    setPropertyGstin('');
    setGstinLoaded(false);
    if (!selectedProperty?.id) {
      setGstinLoaded(true);
      return () => { mounted = false; };
    }
    propertySettingsAPI.get(selectedProperty.id)
      .then((res) => {
        if (!mounted) return;
        setPropertyGstin(res?.RESULT?.gst_number || '');
      })
      .catch(() => { if (mounted) setPropertyGstin(''); })
      .finally(() => { if (mounted) setGstinLoaded(true); });
    return () => { mounted = false; };
  }, [selectedProperty?.id]);

  // iter174 — Fetch admin-controlled GST config (gst_enabled + gst_percentage)
  // for the currently-selected property. Falls open on network / missing
  // keys: `gst_enabled=true, gst_percentage=null` triggers the local per-room
  // slab math as before. When the Admin Panel publishes `gst_enabled=false`
  // the picker is forced to No GST regardless of the user's choice.
  //
  // iter180 — Force cache-bypass (`refresh=true`) every time this screen
  // mounts so the freshest GST slab from the Admin Panel is applied to
  // the walk-in invoice, not a stale value cached by the app backend.
  useEffect(() => {
    let mounted = true;
    if (!selectedProperty?.id) return () => { mounted = false; };
    moduleConfigAPI.get(selectedProperty.id, { refresh: true })
      .then((res) => {
        if (!mounted) return;
        const m = res?.RESULT?.modules || {};
        setGstAdminConfig({
          gst_enabled: m.gst_enabled !== false,     // default true (fail-open)
          // iter181 — Accept any finite gst_percentage ≥ 0 as a valid
          // admin override (0% is meaningful and must not fall back to
          // the per-room slab). Only a genuinely absent / null / blank
          // value keeps the slab default.
          gst_percentage: (() => {
            const raw = m.gst_percentage;
            if (raw === null || raw === undefined || raw === '') return null;
            const n = Number(raw);
            return Number.isFinite(n) && n >= 0 ? n : null;
          })(),
        });
      })
      .catch(() => { /* keep defaults; fail-open */ });
    return () => { mounted = false; };
  }, [selectedProperty?.id]);

  // iter174 — When admin disables GST, force the mode picker to 'none'.
  useEffect(() => {
    if (gstAdminConfig.gst_enabled === false && gstMode !== 'none') {
      setGstMode('none');
    }
  }, [gstAdminConfig.gst_enabled, gstMode]);

  // iter88 Phase D — keep childrenAges array length aligned with no_of_children
  useEffect(() => {
    const n = Math.max(0, parseInt(formData.no_of_children || 0, 10));
    setChildrenAges((prev) => {
      const out = [...(prev || [])];
      while (out.length < n) out.push(0);
      while (out.length > n) out.pop();
      return out;
    });
  }, [formData.no_of_children]);

  // iter88 Phase D / iter91 — derive extraAdultCount from no_of_adults vs
  // total capacity across ALL selected rooms (max_adults × room_count for each).
  useEffect(() => {
    const totalCap = (formData.rooms || []).reduce((acc, sel) => {
      const rt = roomTypes.find(r => r.name === sel.room_name) || {};
      return acc + (Number(rt.max_adults || 2) * Math.max(1, Number(sel.room_count || 1)));
    }, 0);
    const extra = Math.max(0, Number(formData.no_of_adults || 0) - totalCap);
    setExtraAdultCount(extra);
  }, [formData.no_of_adults, formData.rooms, roomTypes]);

  // iter162 — Walk-in Cash now supports GST just like UPI. The previous
  // iter112 hard-lock (force GST OFF when Cash) has been removed. The GST
  // toggle is now user-controlled for BOTH Cash and UPI payment modes;
  // GST math itself is untouched (see pricingBreakdown effect below).

  // Auto-sync whatsapp_number with mobile_number when "same as mobile" is on
  useEffect(() => {
    if (formData.whatsapp_same_as_mobile) {
      const cleanMobile = (formData.mobile_number || '').replace(/\D/g, '').slice(0, 10);
      if (cleanMobile !== formData.whatsapp_number) {
        setFormData(prev => ({ ...prev, whatsapp_number: cleanMobile }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.mobile_number, formData.whatsapp_same_as_mobile]);

  // Calculate pending amount
  const pendingAmount = parseFloat(formData.total_amount || 0) - parseFloat(formData.advance_amount || 0);

  // iter87 Phase C / iter88 Phase D+E / iter91 Multi-room — Auto-fill Total
  // Amount whenever any pricing input changes (rooms list, dates, count,
  // extras, children ages, facilities, GST toggle). Manual edits set
  // `manualOverride=true` and we stop overwriting until the user changes one
  // of the pricing inputs again (which resets the flag to false).
  const roomsKey = JSON.stringify(
    (formData.rooms || []).map(r => ({ n: r.room_name || '', c: r.room_count || 1, r: r.room_rate ?? '' }))
  );
  const selectedFacilitiesKey = JSON.stringify(selectedFacilities);
  const childrenAgesKey = JSON.stringify(childrenAges);
  const pricingKey = `${roomsKey}|${formData.check_in_date}|${formData.check_out_date}|${extraAdultCount}|${childrenAgesKey}|${selectedFacilitiesKey}|m${gstMode}|ae${gstAdminConfig.gst_enabled?1:0}|ap${gstAdminConfig.gst_percentage ?? 'x'}|eb${formData.extra_bed_count || 0}`;

  useEffect(() => {
    // Any pricing-input change → re-allow auto-fill
    setManualOverride(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingKey]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const validRooms = (formData.rooms || []).filter(r => r.room_name && r.room_name.trim());
      if (!selectedProperty?.id || validRooms.length === 0 || !formData.check_in_date || !formData.check_out_date) {
        setPricingBreakdown(null);
        return;
      }
      if (new Date(formData.check_out_date) <= new Date(formData.check_in_date)) {
        setPricingBreakdown(null);
        return;
      }
      setPricingLoading(true);
      setPricingError(null);
      try {
        // iter91 — Multi-room: send ALL selected rooms in a single call.
        const res = await propertyAPI.getPricingMulti(selectedProperty.id, {
          check_in: formData.check_in_date,
          check_out: formData.check_out_date,
          rooms: validRooms.map(r => ({
            room_type: r.room_name.trim(),
            room_count: r.room_count || 1,
          })),
        });
        if (cancelled) return;
        if (res?.STATUS === 1 && res.RESULT) {
          const room = res.RESULT;
          // iter112 — Extra Adult Price MUST come from the Master Form
          // (room_types are enriched by the backend). Distinguish "not
          // configured" (null/undefined) from "explicitly ₹0". A null value
          // is a configuration bug — log it and expose via
          // `pricingBreakdown.extras.extra_adult_missing` so the UI can warn.
          let extraAdultRate = 0;
          let anyMissing = false;
          let anyConfigured = false;
          for (const sel of validRooms) {
            const rt = roomTypes.find(r => r.name === sel.room_name) || {};
            const raw = rt.extra_adult_price;
            if (raw === null || raw === undefined || raw === '') {
              anyMissing = true;
              // eslint-disable-next-line no-console
              console.warn(`[BookingCreate] Master Form → Room "${sel.room_name}" is missing Extra Adult Price. Fill it in Onboarding → Room Inventory.`);
              continue;
            }
            const rate = Number(raw);
            if (Number.isFinite(rate)) {
              anyConfigured = true;
              if (rate > extraAdultRate) extraAdultRate = rate;
            }
          }
          const nights = Number(room.nights || 0);
          const eaCount = Math.max(0, Number(extraAdultCount || 0));
          const extra_adult_charge = Math.round(extraAdultRate * eaCount * nights * 100) / 100;
          // children: <6 free, >=6 charged at extra_adult_price per night
          const childItems = [];
          let extra_child_count = 0;
          let extra_child_charge = 0;
          for (const a of (childrenAges || [])) {
            const n = parseInt(a, 10);
            if (!Number.isFinite(n) || n < 0) continue;
            if (n < 6) {
              childItems.push({ age: n, free: true, charge: 0 });
            } else {
              const c = Math.round(extraAdultRate * nights * 100) / 100;
              childItems.push({ age: n, free: false, charge: c });
              extra_child_count += 1;
              extra_child_charge = Math.round((extra_child_charge + c) * 100) / 100;
            }
          }
          // facilities: locked-at-creation prices from paidFacilities catalogue
          const facItems = [];
          let facilities_total = 0;
          for (const f of paidFacilities) {
            const qty = parseInt(selectedFacilities[f.name] || 0, 10);
            if (qty > 0) {
              const price = Number(f.price || 0);
              const total = Math.round(price * qty * 100) / 100;
              facItems.push({ name: f.name, price, qty, total });
              facilities_total = Math.round((facilities_total + total) * 100) / 100;
            }
          }
          // iter112 — Extra bed: rate from Master Form (enriched into roomTypes)
          // If more than one room type is selected, take the largest bed price.
          let extraBedRate = 0;
          for (const sel of validRooms) {
            const rt = roomTypes.find(r => r.name === sel.room_name) || {};
            const rate = Number(rt.extra_bed_price || 0);
            if (rate > extraBedRate) extraBedRate = rate;
          }
          const ebCount = Math.max(0, Number(formData.extra_bed_count || 0));
          const extra_bed_charge = Math.round(extraBedRate * ebCount * nights * 100) / 100;

          const base_amount = Math.round((Number(room.room_amount || 0) + extra_adult_charge + extra_child_charge + extra_bed_charge + facilities_total) * 100) / 100;

          // iter169/170 — Walk-in GST math via `utils/walkinGst.js`. The
          // engine now understands three modes: 'none' | 'excluding' |
          // 'including'. See helper docstring for the per-room formulas.
          //
          // iter171 — Fallback: when a room's `room_rate` is blank/0, use
          // the master onboarding-form `base_price` for that room type so
          // the pricing summary always shows meaningful numbers, even for
          // Excluding/Including modes. The submit-time validator still
          // blocks bookings that have no rate available anywhere.
          //
          // iter174 — Third argument threads the Admin-Panel dynamic GST
          // config into the pure helper: gst_enabled=false forces No GST,
          // and a positive gst_percentage overrides the per-room slab.
          const walkin = computeWalkinPricing(
            validRooms.map((sel) => {
              const manual = Number(sel.room_rate);
              const master = roomTypes.find(rt => rt.name === sel.room_name);
              const fallback = master?.base_price;
              const effective = Number.isFinite(manual) && manual > 0
                ? manual
                : (fallback != null && fallback !== '' ? Number(fallback) : '');
              return {
                room_type: sel.room_name,
                room_count: sel.room_count,
                rate: effective,
              };
            }),
            nights,
            gstMode,
            gstAdminConfig,
          );
          const perRoom = walkin.per_room;
          const room_charges_total = walkin.room_charges_total;
          const room_gst_total = walkin.room_gst_total;
          const extras_and_facilities = Math.round((extra_adult_charge + extra_child_charge + extra_bed_charge + facilities_total) * 100) / 100;
          const grand_total = Math.round((room_charges_total + room_gst_total + extras_and_facilities) * 100) / 100;
          const gst_amount = room_gst_total;
          const gst_pct = walkin.any_high_slab ? 12 : (perRoom.length ? 5 : 0);
          const gst_enabled = gstMode !== GST_MODES.NONE;

          const full = {
            room,
            extras: {
              extra_adult_per_night: extraAdultRate,
              extra_adult_count: eaCount,
              extra_adult_charge,
              extra_adult_missing: anyMissing && !anyConfigured,
              extra_child_count,
              extra_child_charge,
              children: childItems,
              // iter112 — extra beds
              extra_bed_price: extraBedRate,
              extra_bed_count: ebCount,
              extra_bed_charge,
            },
            facilities: { items: facItems, total: facilities_total },
            gst_enabled,
            gst_mode: walkin.gst_mode,
            gst_pct,
            gst_amount,
            base_amount,
            grand_total,
            // iter169 — per-room manual-rate breakdown for the UI summary.
            per_room: perRoom,
            room_charges_total,
            room_gst_total,
            nights,
            // Sum max_adults across selected rooms (×room_count each)
            max_adults: validRooms.reduce((acc, sel) => {
              const rt = roomTypes.find(r => r.name === sel.room_name) || {};
              return acc + (Number(rt.max_adults || 2) * Math.max(1, Number(sel.room_count || 1)));
            }, 0),
          };
          setPricingBreakdown(full);
          if (!manualOverride) {
            setFormData((prev) => ({
              ...prev,
              total_amount: String(grand_total || 0),
            }));
            if (errors.total_amount) {
              setErrors((prev) => ({ ...prev, total_amount: null }));
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPricingError(e?._friendlyMessage || e?.detail || 'Unable to fetch pricing');
          setPricingBreakdown(null);
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingKey, selectedProperty?.id, roomTypes, paidFacilities]);

  // iter91 — Live room availability per selected (room_type, dates).
  // Map keyed by room_type name → { total, available, sold }.
  const [availabilityMap, setAvailabilityMap] = useState({});
  const availabilityKey = `${roomsKey}|${formData.check_in_date}|${formData.check_out_date}`;
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedProperty?.id || !formData.check_in_date || !formData.check_out_date) {
        setAvailabilityMap({});
        return;
      }
      if (new Date(formData.check_out_date) <= new Date(formData.check_in_date)) {
        setAvailabilityMap({});
        return;
      }
      const unique = Array.from(new Set(
        (formData.rooms || []).map(r => (r.room_name || '').trim()).filter(Boolean)
      ));
      const next = {};
      await Promise.all(unique.map(async (name) => {
        try {
          const res = await propertyAPI.getAvailability(selectedProperty.id, {
            room_type: name,
            check_in: formData.check_in_date,
            check_out: formData.check_out_date,
          });
          if (!cancelled && res?.STATUS === 1) {
            next[name] = res.RESULT;
          }
        } catch (e) {
          // Soft-fail — leave undefined; "+" button falls back to unrestricted
        }
      }));
      if (!cancelled) setAvailabilityMap(next);
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityKey, selectedProperty?.id]);

  // iter91 — Clamp any room_count that exceeds available inventory (e.g.
  // when the user changes the date range to a more crowded window).
  useEffect(() => {
    let changed = false;
    const updated = (formData.rooms || []).map((r) => {
      const avail = availabilityMap[r.room_name];
      if (avail && Number.isFinite(avail.available) && r.room_count > avail.available) {
        changed = true;
        return { ...r, room_count: Math.max(1, avail.available) };
      }
      return r;
    });
    if (changed) {
      setFormData((prev) => ({ ...prev, rooms: updated }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityMap]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleRoomChange = (index, field, value) => {
    const newRooms = [...formData.rooms];
    newRooms[index] = { ...newRooms[index], [field]: value };
    setFormData(prev => ({ ...prev, rooms: newRooms }));
    if (errors.rooms) {
      setErrors(prev => ({ ...prev, rooms: null }));
    }
  };

  const addRoom = () => {
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, { room_name: '', room_count: 1, room_rate: '' }]
    }));
  };

  // Dropdown selection handler — room types come from the Onboarding Form.
  // iter169 — Also seeds `room_rate` with the master `base_price` so users
  // start from the configured rate; they can edit freely from there.
  const handleRoomTypeSelect = (index, value) => {
    const newRooms = [...formData.rooms];
    const master = roomTypes.find(r => r.name === value) || {};
    const seedRate = master.base_price != null && master.base_price !== ''
      ? String(master.base_price)
      : '';
    newRooms[index] = { ...newRooms[index], room_name: value, room_rate: seedRate };
    setFormData(prev => ({ ...prev, rooms: newRooms }));
    if (errors.rooms) setErrors(prev => ({ ...prev, rooms: null }));
  };

  const removeRoom = (index) => {
    if (formData.rooms.length > 1) {
      const newRooms = formData.rooms.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, rooms: newRooms }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Guest name validation
    if (!formData.guest_name || formData.guest_name.trim().length < 2) {
      newErrors.guest_name = 'Guest name must be at least 2 characters';
    }
    
    // Mobile number validation
    const cleanMobile = formData.mobile_number.replace(/\D/g, '');
    if (cleanMobile.length !== 10) {
      newErrors.mobile_number = 'Mobile number must be exactly 10 digits';
    }
    
    // Room validation
    const hasValidRoom = formData.rooms.some(r => r.room_name && r.room_name.trim() && r.room_count >= 1);
    if (!hasValidRoom) {
      newErrors.rooms = 'At least one room with a name is required';
    }

    // iter171 — When GST is applied, every named room must resolve to a
    // usable rate — either the manually-entered `room_rate` or the master
    // Onboarding-Form `base_price` fallback. Blocks silent zero-rate
    // bookings where GST would otherwise round to ₹0.
    if (gstMode !== 'none') {
      const roomsMissingRate = (formData.rooms || [])
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => r.room_name && r.room_name.trim())
        .filter(({ r }) => {
          const manual = Number(r.room_rate);
          if (Number.isFinite(manual) && manual > 0) return false;
          const master = roomTypes.find(rt => rt.name === r.room_name);
          const fallback = Number(master?.base_price);
          return !(Number.isFinite(fallback) && fallback > 0);
        });
      if (roomsMissingRate.length > 0) {
        const names = roomsMissingRate.map(({ r }) => r.room_name).join(', ');
        newErrors.rooms = `Enter a room rate for: ${names}. GST needs a per-room rate to compute the ${gstMode === 'excluding' ? 'tax' : 'taxable amount'}.`;
      }
    }

    // Total amount validation
    const totalAmount = parseFloat(formData.total_amount);
    if (!totalAmount || totalAmount <= 0) {
      newErrors.total_amount = 'Total amount must be greater than 0';
    }
    
    // Advance amount validation
    const advanceAmount = parseFloat(formData.advance_amount || 0);
    if (advanceAmount > totalAmount) {
      newErrors.advance_amount = 'Advance cannot exceed total amount';
    }
    
    // Date validation
    if (new Date(formData.check_out_date) < new Date(formData.check_in_date)) {
      newErrors.check_out_date = 'Check-out must be on or after check-in date';
    }
    
    // Booked on date validation
    if (!formData.booked_on_date) {
      newErrors.booked_on_date = 'Booked on date is required';
    }
    
    // WhatsApp validation — skip entirely when the field is hidden
    if (fieldShown('whatsapp_number') && formData.send_whatsapp) {
      const cleanWhatsapp = (formData.whatsapp_number || '').replace(/\D/g, '');
      if (cleanWhatsapp.length !== 10) {
        newErrors.whatsapp_number = 'WhatsApp number must be exactly 10 digits';
      }
    }

    // iter189 — Panel-driven required flags for the optional field set.
    if (fieldShown('no_of_children') && fieldRequired('no_of_children')
        && !(parseInt(formData.no_of_children || 0, 10) > 0)) {
      newErrors.no_of_children = 'Number of children is required';
    }
    if (fieldShown('advance_amount') && fieldRequired('advance_amount')
        && !(parseFloat(formData.advance_amount || 0) > 0)) {
      newErrors.advance_amount = 'Advance amount is required';
    }
    if (fieldShown('extra_bed_count') && fieldRequired('extra_bed_count')
        && !(parseInt(formData.extra_bed_count || 0, 10) > 0)) {
      newErrors.extra_bed_count = 'Extra bed count is required';
    }
    if (fieldShown('whatsapp_number') && fieldRequired('whatsapp_number')
        && !formData.send_whatsapp) {
      newErrors.whatsapp_number = 'WhatsApp notification is required';
    }
    if (fieldShown('gst_number') && fieldRequired('gst_number')
        && gstMode !== 'none' && !(propertyGstin || '').trim()) {
      newErrors.hotel_gstin = 'Property GSTIN is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before submitting',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const facilitiesPayload = (paidFacilities || [])
        .filter(f => parseInt(selectedFacilities[f.name] || 0, 10) > 0)
        .map(f => ({
          name: f.name,
          price: Number(f.price || 0),  // lock current price at booking time
          qty: parseInt(selectedFacilities[f.name] || 1, 10),
        }));

      const bookingData = {
        property_id: selectedProperty.id,
        guest_name: formData.guest_name.trim(),
        mobile_number: formData.mobile_number.replace(/\D/g, ''),
        rooms: formData.rooms.filter(r => r.room_name && r.room_name.trim()).map(r => ({
          room_name: r.room_name.trim(),
          room_count: parseInt(r.room_count) || 1,
          // iter169 — Manual per-room rate captured with the booking so the
          // backend/invoice reflects exactly what was billed.
          room_rate: r.room_rate !== '' && r.room_rate != null ? Number(r.room_rate) : null,
        })),
        payment_mode: formData.payment_mode,
        advance_amount: parseFloat(formData.advance_amount) || 0,
        total_amount: parseFloat(formData.total_amount),
        check_in_date: formData.check_in_date,
        check_out_date: formData.check_out_date,
        booked_on_date: formData.booked_on_date,
        no_of_adults: parseInt(formData.no_of_adults || 1, 10),
        no_of_children: parseInt(formData.no_of_children || 0, 10),
        send_whatsapp: formData.send_whatsapp,
        whatsapp_number: formData.send_whatsapp ? formData.whatsapp_number.replace(/\D/g, '') : null,
        // iter278 — Optional "send menu PDF" after voucher.
        send_menu: !!formData.send_menu,
        // iter88 Phase D/E — itemized inputs
        children_ages: childrenAges.slice(0, formData.no_of_children || 0).map(a => parseInt(a || 0, 10)),
        extra_adult_count: extraAdultCount,
        // iter112 — Extra beds
        extra_bed_count: Math.max(0, parseInt(formData.extra_bed_count || 0, 10) || 0),
        extra_bed_price: Number(pricingBreakdown?.extras?.extra_bed_price || 0),
        extra_bed_charge: Number(pricingBreakdown?.extras?.extra_bed_charge || 0),
        facilities: facilitiesPayload,
        // iter170 — Legacy `gst_enabled` mirrors the mode for backward-compat.
        gst_enabled: gstMode !== 'none',
        gst_mode: gstMode,                          // 'none' | 'excluding' | 'including'
        hotel_gstin: gstMode !== 'none' ? propertyGstin : '',
        pricing_mode: manualOverride ? 'manual' : 'auto',
        // Persist the client's per-room breakdown so the backend can render
        // the mode-aware walk-in invoice even before server-side math catches
        // up. The backend will still recompute defensively when it can.
        walkin_breakdown: pricingBreakdown ? {
          gst_mode: pricingBreakdown.gst_mode || 'none',
          nights: pricingBreakdown.nights || 0,
          per_room: pricingBreakdown.per_room || [],
          room_charges_total: pricingBreakdown.room_charges_total || 0,
          room_gst_total: pricingBreakdown.room_gst_total || 0,
          grand_total: pricingBreakdown.grand_total || 0,
          hotel_gstin: gstMode !== 'none' ? propertyGstin : '',
        } : null,
      };

      const response = await bookingAPI.createBooking(bookingData);
      
      if (response.STATUS === 1) {
        setCreatedBookingId(response.RESULT.booking_id);
        setShowSuccess(true);
        toast({ 
          title: 'Success', 
          description: `Booking Created Successfully!\nBooking ID: ${response.RESULT.booking_id}` 
        });
      }
    } catch (error) {
      // Axios interceptor sets _friendlyMessage for network/timeout/server errors.
      // For FastAPI validation errors (422), error.detail can be array or string.
      let errorMessage = 'Failed to create booking';

      if (error?._friendlyMessage) {
        errorMessage = error._friendlyMessage;
      } else if (typeof error?.detail === 'string') {
        errorMessage = error.detail;
      } else if (Array.isArray(error?.detail) && error.detail.length > 0) {
        // Pydantic 422 validation error array
        const first = error.detail[0];
        const fieldPath = (first.loc || []).filter(p => p !== 'body').join('.');
        errorMessage = fieldPath ? `${fieldPath}: ${first.msg}` : first.msg;
      } else if (error?.message) {
        errorMessage = error.message === 'Network Error'
          ? 'Server unavailable. Please check your connection and try again.'
          : error.message;
      }

      toast({
        title: 'Booking Not Created',
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Booking creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate('/');
  };

  // Success Modal
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center animate-fadeIn">
          <div className="w-20 h-20 bg-[#EEF1FC] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#1B2A7B]" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Booking Created Successfully</h2>
          <p className="text-gray-600 mb-2">Your booking has been confirmed</p>
          <p className="text-lg font-medium text-navy mb-6">
            Booking ID: <span className="text-[#1B2A7B]">{createdBookingId}</span>
          </p>
          <button
            onClick={handleSuccessClose}
            className="w-full py-3 bg-[#1B2A7B] text-white font-medium rounded-lg hover:bg-[#1B2A7B]-dark transition-colors"
            data-testid="success-close-btn"
          >
            Back to Calendar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32" data-testid="booking-create-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-navy shadow-md px-4 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-navy-light text-white hover:bg-navy-dark transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-white">New Booking</h1>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Guest Info Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-[#1B2A7B]" />
            Guest Information
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Guest Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.guest_name}
                onChange={(e) => handleChange('guest_name', e.target.value)}
                placeholder="Enter guest name"
                className={`w-full px-4 py-3 bg-gray-50 rounded-lg border ${errors.guest_name ? 'border-red-400' : 'border-gray-200'} text-gray-900 placeholder-gray-400 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                data-testid="guest-name-input"
              />
              {errors.guest_name && (
                <p className="text-red-500 text-xs mt-1">{errors.guest_name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => handleChange('mobile_number', e.target.value)}
                  placeholder="10 digit mobile number"
                  maxLength={10}
                  className={`w-full pl-12 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.mobile_number ? 'border-red-400' : 'border-gray-200'} text-gray-900 placeholder-gray-400 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                  data-testid="mobile-input"
                />
              </div>
              {errors.mobile_number && (
                <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>
              )}
            </div>
          </div>
        </div>

        {/* iter192 — Booking Dates moved to sit between Guest Info and Rooms. */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1B2A7B]" />
            Booking Dates
          </h3>

          <div className="space-y-4">
            {/* Booked On Date */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                <CalendarPlus className="w-4 h-4" />
                Booked On <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.booked_on_date}
                onChange={(e) => handleChange('booked_on_date', e.target.value)}
                className={`w-full px-3 py-3 bg-gray-50 rounded-lg border ${errors.booked_on_date ? 'border-red-400' : 'border-gray-200'} text-gray-900 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                data-testid="booked-on-input"
              />
              {errors.booked_on_date && (
                <p className="text-red-500 text-xs mt-1">{errors.booked_on_date}</p>
              )}
            </div>

            {/* Check-in and Check-out */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Check-in</label>
                <input
                  type="date"
                  value={formData.check_in_date}
                  onChange={(e) => handleChange('check_in_date', e.target.value)}
                  className="w-full px-3 py-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-900 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors"
                  data-testid="check-in-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Check-out</label>
                <input
                  type="date"
                  value={formData.check_out_date}
                  onChange={(e) => handleChange('check_out_date', e.target.value)}
                  className={`w-full px-3 py-3 bg-gray-50 rounded-lg border ${errors.check_out_date ? 'border-red-400' : 'border-gray-200'} text-gray-900 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                  data-testid="check-out-input"
                />
                {errors.check_out_date && (
                  <p className="text-red-500 text-xs mt-1">{errors.check_out_date}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rooms Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Home className="w-4 h-4 text-[#1B2A7B]" />
              Rooms <span className="text-red-500">*</span>
            </h3>
            <button
              onClick={addRoom}
              className="flex items-center gap-1 text-[#1B2A7B] text-sm font-medium hover:text-[#1B2A7B]-dark transition-colors"
              data-testid="add-room-btn"
            >
              <Plus className="w-4 h-4" />
              Add Room
            </button>
          </div>
          
          {errors.rooms && (
            <p className="text-red-500 text-xs mb-3">{errors.rooms}</p>
          )}

          {roomTypes.length === 0 && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="no-room-types-hint">
              <ClipboardList className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                No room types configured yet. Add room categories in the <span className="font-semibold">Onboarding Form</span> (Profile tab) to enable selection here.
              </p>
            </div>
          )}
          
          <div className="space-y-3">
            {formData.rooms.map((room, index) => {
              // iter91 — Availability-aware controls
              const avail = availabilityMap[room.room_name];
              const availableRooms = avail ? Number(avail.available || 0) : null;
              const totalInventory = avail ? Number(avail.total || 0) : null;
              const atCap = availableRooms !== null && room.room_count >= availableRooms;
              return (
              <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2">
                <div className="flex items-center gap-3 min-w-0">
                  <select
                    value={room.room_name || ''}
                    onChange={(e) => handleRoomTypeSelect(index, e.target.value)}
                    disabled={roomTypes.length === 0}
                    /* iter192 — `min-w-0` lets flex-1 actually constrain the
                       select to the remaining row width; `truncate` clips the
                       closed-state selected value with an ellipsis so long
                       room names never overflow the container. The dropdown
                       popup is browser-controlled and wraps long options
                       natively on all major browsers. */
                    style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                    className="flex-1 min-w-0 truncate px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-900 text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors disabled:opacity-60"
                    data-testid={`room-type-select-${index}`}
                  >
                    <option value="" disabled>Select room type</option>
                    {roomTypes.map((rt) => {
                      // Look up live availability for this room_type (only when dates are set)
                      const a = availabilityMap[rt.name];
                      const ra = a ? Number(a.available || 0) : null;
                      const soldOut = ra !== null && ra <= 0;
                      return (
                        <option
                          key={rt.id}
                          value={rt.name}
                          disabled={soldOut && rt.name !== room.room_name}
                          data-testid={`room-type-option-${rt.name.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          {rt.name}{ra !== null ? ` (${ra} avail)` : ''}{soldOut ? ' — Sold out' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-2">
                    <button
                      onClick={() => handleRoomChange(index, 'room_count', Math.max(1, room.room_count - 1))}
                      className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                      data-testid={`room-minus-${index}`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-medium text-gray-900" data-testid={`room-count-${index}`}>{room.room_count}</span>
                    <button
                      onClick={() => {
                        if (atCap) return;  // iter91 — hard-stop at available inventory
                        handleRoomChange(index, 'room_count', room.room_count + 1);
                      }}
                      disabled={atCap}
                      className={`p-1 transition-colors ${atCap ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-[#1B2A7B]'}`}
                      data-testid={`room-plus-${index}`}
                      title={atCap ? `Only ${availableRooms} rooms available` : ''}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {formData.rooms.length > 1 && (
                    <button
                      onClick={() => removeRoom(index)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`room-remove-${index}`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* iter91 — Live availability hint */}
                {avail && (
                  <div className="text-xs text-gray-500 flex items-center gap-2" data-testid={`room-availability-${index}`}>
                    <span>
                      {availableRooms > 0
                        ? <>Available: <span className="font-semibold text-[#1B2A7B]">{availableRooms}</span> of {totalInventory}</>
                        : <span className="font-semibold text-red-600">Sold out for selected dates</span>}
                    </span>
                    {atCap && availableRooms > 0 && (
                      <span className="text-amber-700">— max reached</span>
                    )}
                  </div>
                )}
                {/* iter169 — Manual per-room rate. Seeded from Onboarding Form
                    base_price; freely editable. Drives GST slab + charges. */}
                {room.room_name && (
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 flex-shrink-0 w-24">Room Rate</label>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={room.room_rate ?? ''}
                        onChange={(e) => handleRoomChange(index, 'room_rate', e.target.value)}
                        placeholder="per room / night"
                        className="w-full pl-7 pr-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-900 text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors"
                        data-testid={`room-rate-input-${index}`}
                      />
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">/night</span>
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>

        {/* Number of Guests */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1B2A7B]" />
            Number of Guests
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {/* Adults */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Adults</label>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                <button
                  onClick={() => handleChange('no_of_adults', Math.max(1, formData.no_of_adults - 1))}
                  className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                  data-testid="adults-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="flex-1 text-center font-semibold text-gray-900" data-testid="adults-count">{formData.no_of_adults}</span>
                <button
                  onClick={() => handleChange('no_of_adults', formData.no_of_adults + 1)}
                  className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                  data-testid="adults-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Children — hide-able via panel walk-in-form-config */}
            {fieldShown('no_of_children') && (
              <div data-testid="field-no-of-children">
                <label className="text-xs text-gray-500 mb-1 block">
                  Children{fieldRequired('no_of_children') ? ' *' : ''}
                </label>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                  <button
                    onClick={() => handleChange('no_of_children', Math.max(0, formData.no_of_children - 1))}
                    className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                    data-testid="children-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="flex-1 text-center font-semibold text-gray-900" data-testid="children-count">{formData.no_of_children}</span>
                  <button
                    onClick={() => handleChange('no_of_children', formData.no_of_children + 1)}
                    className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                    data-testid="children-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* iter88 Phase D — Children ages (only when >0 AND field shown) */}
          {fieldShown('no_of_children') && formData.no_of_children > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="text-xs text-gray-500 mb-2 block">
                Children ages <span className="text-gray-400">(below 6 years = free; 6 and above = charged at extra-adult rate)</span>
              </label>
              <div className="grid grid-cols-3 gap-2" data-testid="children-ages-grid">
                {Array.from({ length: formData.no_of_children }).map((_, i) => (
                  <input
                    key={i}
                    type="number"
                    min="0"
                    max="17"
                    value={childrenAges[i] ?? ''}
                    onChange={(e) => {
                      const v = parseInt(e.target.value || 0, 10);
                      setChildrenAges((prev) => {
                        const out = [...prev];
                        out[i] = Number.isFinite(v) ? v : 0;
                        return out;
                      });
                    }}
                    placeholder={`Child ${i + 1} age`}
                    className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald"
                    data-testid={`child-age-${i}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* iter88 Phase D — Extra adults indicator (iter112 — warn when Master Form price is missing) */}
          {extraAdultCount > 0 && pricingBreakdown && (
            pricingBreakdown.extras?.extra_adult_missing ? (
              <div
                className="mt-3 px-3 py-2 bg-red-50 border border-red-300 rounded-md text-xs text-red-800"
                data-testid="extra-adults-missing-warning"
              >
                <span className="font-semibold">⚠ {extraAdultCount} extra adult{extraAdultCount > 1 ? 's' : ''}</span> beyond room capacity — but the <strong>Extra Adult Price is not configured</strong> in the Master Form. Please go to <em>Profile → Onboarding → Room Inventory</em> and set the price. Extras are not being charged.
              </div>
            ) : (
              <div
                className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800"
                data-testid="extra-adults-indicator"
              >
                <span className="font-semibold">{extraAdultCount} extra adult{extraAdultCount > 1 ? 's' : ''}</span> beyond room capacity — charged at ₹{Number(pricingBreakdown.extras?.extra_adult_per_night || 0).toLocaleString()} / night × {pricingBreakdown.room?.nights} = ₹{Number(pricingBreakdown.extras?.extra_adult_charge || 0).toLocaleString()}
              </div>
            )
          )}

          {/* iter112 — Extra Bed stepper (hide-able via panel walk-in-form-config) */}
          {fieldShown('extra_bed_count') && (
            <div className="mt-4 pt-4 border-t border-gray-100" data-testid="field-extra-bed-count">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Extra Beds{fieldRequired('extra_bed_count') ? ' *' : ''}
                  </label>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {pricingBreakdown?.extras?.extra_bed_price > 0
                      ? `₹${Number(pricingBreakdown.extras.extra_bed_price).toLocaleString()} per bed / night`
                      : 'Set price in Onboarding → Room Inventory'}
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                  <button
                    onClick={() => handleChange('extra_bed_count', Math.max(0, Number(formData.extra_bed_count || 0) - 1))}
                    className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                    data-testid="extra-bed-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-semibold text-gray-900" data-testid="extra-bed-count">
                    {Number(formData.extra_bed_count || 0)}
                  </span>
                  <button
                    onClick={() => handleChange('extra_bed_count', Number(formData.extra_bed_count || 0) + 1)}
                    className="p-1 text-gray-500 hover:text-[#1B2A7B] transition-colors"
                    data-testid="extra-bed-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {Number(formData.extra_bed_count || 0) > 0 && pricingBreakdown?.extras?.extra_bed_charge > 0 && (
                <div
                  className="mt-2 px-3 py-2 bg-[#EEF1FC] border border-[#1B2A7B]/30 rounded-md text-xs text-[#1B2A7B]"
                  data-testid="extra-beds-indicator"
                >
                  <span className="font-semibold">{formData.extra_bed_count} extra bed{formData.extra_bed_count > 1 ? 's' : ''}</span> · ₹{Number(pricingBreakdown.extras.extra_bed_price).toLocaleString()} × {pricingBreakdown.room?.nights} night(s) = ₹{Number(pricingBreakdown.extras.extra_bed_charge).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* iter88 Phase D — Paid Facilities */}
        {paidFacilities.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="paid-facilities-section">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#1B2A7B]" />
              Paid Facilities <span className="text-xs font-normal text-gray-400">(prices locked at booking)</span>
            </h3>
            <div className="space-y-2">
              {paidFacilities.map((f) => {
                const qty = parseInt(selectedFacilities[f.name] || 0, 10);
                const checked = qty > 0;
                return (
                  <label
                    key={f.name}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${checked ? 'border-[#1B2A7B]/40 bg-[#EEF1FC]/40' : 'border-gray-200 bg-gray-50'} cursor-pointer transition-colors`}
                    data-testid={`facility-row-${f.name.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedFacilities((prev) => {
                            const out = { ...prev };
                            if (e.target.checked) out[f.name] = 1;
                            else delete out[f.name];
                            return out;
                          });
                        }}
                        className="w-4 h-4 text-[#1B2A7B] rounded border-gray-300 focus:ring-emerald"
                        data-testid={`facility-checkbox-${f.name.replace(/\s+/g,'-').toLowerCase()}`}
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{f.name}</div>
                        <div className="text-xs text-gray-500">₹{Number(f.price || 0).toLocaleString()} each</div>
                      </div>
                    </div>
                    {checked && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedFacilities((prev) => {
                              const out = { ...prev };
                              const next = Math.max(0, (parseInt(out[f.name] || 0, 10) - 1));
                              if (next <= 0) delete out[f.name];
                              else out[f.name] = next;
                              return out;
                            });
                          }}
                          className="p-1 text-gray-500 hover:text-[#1B2A7B]"
                          data-testid={`facility-minus-${f.name.replace(/\s+/g,'-').toLowerCase()}`}
                        ><Minus className="w-4 h-4" /></button>
                        <span className="w-6 text-center text-sm font-semibold" data-testid={`facility-qty-${f.name.replace(/\s+/g,'-').toLowerCase()}`}>{qty}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedFacilities((prev) => ({ ...prev, [f.name]: (parseInt(prev[f.name] || 0, 10) + 1) }));
                          }}
                          className="p-1 text-gray-500 hover:text-[#1B2A7B]"
                          data-testid={`facility-plus-${f.name.replace(/\s+/g,'-').toLowerCase()}`}
                        ><Plus className="w-4 h-4" /></button>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
            {pricingBreakdown?.facilities?.total > 0 && (
              <div className="mt-3 text-right text-sm font-semibold text-[#1B2A7B]" data-testid="facilities-total">
                Facilities Total: ₹{Number(pricingBreakdown.facilities.total).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {/* Payment Section - Updated */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#1B2A7B]" />
            Payment Details
          </h3>
          
          <div className="space-y-4">
            {/* Payment Mode */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleChange('payment_mode', 'UPI')}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  formData.payment_mode === 'UPI'
                    ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid="payment-upi"
              >
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">UPI</span>
              </button>
              <button
                onClick={() => handleChange('payment_mode', 'Cash')}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  formData.payment_mode === 'Cash'
                    ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid="payment-cash"
              >
                <Banknote className="w-5 h-5" />
                <span className="font-medium">Cash</span>
              </button>
            </div>
            
            {/* iter170 — GST mode picker (replaces the old on/off toggle).
                Three mutually-exclusive options: No GST / Excluding / Including.
                Default is 'none'. Shown for BOTH UPI and Cash. */}
            {(formData.payment_mode === 'UPI' || formData.payment_mode === 'Cash') && (
              <div className="space-y-2" data-testid="gst-mode-picker">
                <div className="text-sm font-medium text-gray-900 flex items-center justify-between">
                  <span>GST</span>
                  {gstMode !== 'none' && pricingBreakdown?.room_gst_total > 0 && (
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        Tax {pricingBreakdown.per_room?.some(r => r.gst_pct === 12) && pricingBreakdown.per_room?.some(r => r.gst_pct === 5) ? '(mixed)' : `@ ${pricingBreakdown.gst_pct}%`}
                      </div>
                      <div className="text-sm font-semibold text-[#1B2A7B]" data-testid="gst-amount">₹{Number(pricingBreakdown.room_gst_total).toLocaleString()}</div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'none',      label: 'No GST',        hint: 'GST not applied' },
                    { value: 'excluding', label: 'Excluding GST', hint: 'Add GST on top' },
                    { value: 'including', label: 'Including GST', hint: 'Rate has GST inside' },
                  ].map((opt) => {
                    const active = gstMode === opt.value;
                    // iter174 — Admin has disabled GST for this property.
                    // Only 'none' stays selectable; other tiles show a
                    // disabled/dimmed state.
                    const forced = gstAdminConfig.gst_enabled === false;
                    const disabled = forced && opt.value !== 'none';
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && setGstMode(opt.value)}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                          active
                            ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]'
                            : disabled
                              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        data-testid={`gst-mode-${opt.value}`}
                        aria-pressed={active}
                        aria-disabled={disabled}
                      >
                        <div className="text-sm font-semibold leading-tight">{opt.label}</div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>

                {/* GSTIN display / configure hint when a GST mode is active. */}
                {gstMode !== 'none' && (
                  <div
                    className="mt-1 p-3 rounded-lg border border-[#1B2A7B]/20 bg-[#EEF1FC]/60 flex items-center justify-between gap-3"
                    data-testid="booking-gstin-block"
                  >
                    {propertyGstin ? (
                      <>
                        <div className="min-w-0">
                          <div className="text-[11px] text-gray-500">Hotel GSTIN</div>
                          <div className="font-mono text-sm font-semibold text-[#1B2A7B] tracking-wide truncate" data-testid="booking-gstin-value">
                            {propertyGstin}
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-500 flex-shrink-0">Auto-filled from Profile</span>
                      </>
                    ) : (
                      <>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-amber-700">GST Number not configured</div>
                          <div className="text-[11px] text-gray-500">Add it in Profile so it appears on your invoices.</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate('/profile')}
                          className="flex-shrink-0 px-3 py-1.5 rounded-md bg-[#1B2A7B] text-white text-xs font-medium hover:bg-[#152163]"
                          data-testid="booking-goto-profile-btn"
                        >
                          Go to Profile
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="text-[11px] text-gray-500" data-testid="gst-slab-hint">
                  {gstAdminConfig.gst_enabled === false
                    ? 'GST is disabled by the admin for this property.'
                    : (gstAdminConfig.gst_percentage
                        ? `Rate: ${gstAdminConfig.gst_percentage}% (set by admin, applied to every room).`
                        : 'Slab: 5% if room rate < ₹7,000 · 12% if room rate ≥ ₹7,000 (per room, per night).')}
                </div>
              </div>
            )}

            {/* Total Amount */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-600">
                  Total Amount <span className="text-red-500">*</span>
                </label>
                {manualOverride && pricingBreakdown && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full"
                    data-testid="manual-override-indicator"
                    title={`Auto-calculated value was ₹${(pricingBreakdown.room_amount || 0).toLocaleString()}`}
                  >
                    Manual Override
                  </span>
                )}
                {!manualOverride && pricingBreakdown && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide bg-[#EEF1FC] text-[#1B2A7B] border border-[#1B2A7B]/30 px-2 py-0.5 rounded-full"
                    data-testid="auto-calculated-indicator"
                  >
                    Auto-calculated
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  value={formData.total_amount}
                  onChange={(e) => {
                    setManualOverride(true);
                    handleChange('total_amount', e.target.value);
                  }}
                  placeholder="Enter total amount"
                  min="1"
                  className={`w-full pl-10 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.total_amount ? 'border-red-400' : 'border-gray-200'} text-gray-900 placeholder-gray-400 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                  data-testid="total-amount-input"
                />
              </div>
              {errors.total_amount && (
                <p className="text-red-500 text-xs mt-1">{errors.total_amount}</p>
              )}
              {pricingBreakdown && (
                <div className="mt-3 text-xs text-gray-700 space-y-2 bg-gray-50 border border-gray-100 rounded-md p-3" data-testid="pricing-breakdown-summary">
                  {/* iter170 — Mode-aware header */}
                  <div className="flex items-center justify-between pb-1 border-b border-gray-200 -mx-1 px-1" data-testid="breakdown-mode-header">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#1B2A7B]">
                      {pricingBreakdown.gst_mode === 'excluding' && 'Excluding GST'}
                      {pricingBreakdown.gst_mode === 'including' && 'Including GST'}
                      {(pricingBreakdown.gst_mode === 'none' || !pricingBreakdown.gst_mode) && 'No GST'}
                    </span>
                    {pricingBreakdown.gst_mode !== 'none' && propertyGstin && (
                      <span className="text-[11px] text-gray-500 font-mono" data-testid="breakdown-gstin">
                        GSTIN: {propertyGstin}
                      </span>
                    )}
                  </div>
                  {/* iter169/170 — Per-room-type breakdown with independent GST slab. */}
                  {(pricingBreakdown.per_room || []).map((pr, idx) => (
                    <div key={idx} className="pb-2 border-b border-gray-200 last:border-b-0 last:pb-0" data-testid={`per-room-row-${idx}`}>
                      <div className="flex justify-between font-semibold text-gray-800">
                        <span data-testid={`per-room-label-${idx}`}>
                          {pr.room_type} ({pr.room_count} Room{pr.room_count > 1 ? 's' : ''}) @ ₹{Number(pr.rate || 0).toLocaleString()}/night
                        </span>
                      </div>
                      {pr.rate_missing ? (
                        <div className="text-amber-700 mt-1" data-testid={`per-room-missing-${idx}`}>
                          Enter a room rate to include this room in charges.
                        </div>
                      ) : pricingBreakdown.gst_mode === 'including' ? (
                        <div className="mt-1 space-y-0.5 pl-1">
                          <div className="flex justify-between">
                            <span>Incl. rate: ₹{Number(pr.rate).toLocaleString()} × {pr.room_count} × {pr.nights} night{pr.nights > 1 ? 's' : ''}</span>
                            <span data-testid={`per-room-total-${idx}`}>₹{Number(pr.total_charges).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Taxable amount</span>
                            <span data-testid={`per-room-taxable-${idx}`}>₹{Number(pr.charges_base).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Tax component ({pr.gst_pct}%)</span>
                            <span data-testid={`per-room-gst-${idx}`}>₹{Number(pr.gst_amount).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Total per night</span>
                            <span data-testid={`per-room-total-per-night-${idx}`}>₹{Number(pr.total_per_night).toLocaleString()}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 space-y-0.5 pl-1">
                          <div className="flex justify-between">
                            <span>Room charges: ₹{Number(pr.rate).toLocaleString()} × {pr.room_count} × {pr.nights} night{pr.nights > 1 ? 's' : ''}</span>
                            <span data-testid={`per-room-charges-${idx}`}>₹{Number(pr.charges_base).toLocaleString()}</span>
                          </div>
                          {pricingBreakdown.gst_mode === 'excluding' && (
                            <div className="flex justify-between">
                              <span>Tax ({pr.gst_pct}%)</span>
                              <span data-testid={`per-room-gst-${idx}`}>₹{Number(pr.gst_amount).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-gray-500">
                            <span>Total per night</span>
                            <span data-testid={`per-room-total-per-night-${idx}`}>₹{Number(pr.total_per_night).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {pricingBreakdown.extras?.extra_adult_charge > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-extra-adult">
                      <span>Extra Adults ({pricingBreakdown.extras.extra_adult_count})</span>
                      <span>₹{Number(pricingBreakdown.extras.extra_adult_charge).toLocaleString()}</span>
                    </div>
                  )}
                  {pricingBreakdown.extras?.extra_child_charge > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-extra-child">
                      <span>Extra Children ({pricingBreakdown.extras.extra_child_count})</span>
                      <span>₹{Number(pricingBreakdown.extras.extra_child_charge).toLocaleString()}</span>
                    </div>
                  )}
                  {pricingBreakdown.extras?.extra_bed_charge > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-extra-bed">
                      <span>Extra Beds ({pricingBreakdown.extras.extra_bed_count})</span>
                      <span>₹{Number(pricingBreakdown.extras.extra_bed_charge).toLocaleString()}</span>
                    </div>
                  )}
                  {(pricingBreakdown.facilities?.items || []).map((f, idx) => (
                    <div key={idx} className="flex justify-between" data-testid={`breakdown-facility-${idx}`}>
                      <span>{f.name} (×{f.qty})</span>
                      <span>₹{Number(f.total).toLocaleString()}</span>
                    </div>
                  ))}

                  {/* iter169/170 — Totals section, mode-aware */}
                  <div className="flex justify-between pt-2 mt-1 border-t border-gray-200 text-gray-800" data-testid="breakdown-room-subtotal">
                    <span>{pricingBreakdown.gst_mode === 'including' ? 'Taxable amount' : 'Room subtotal (without tax)'}</span>
                    <span>₹{Number(pricingBreakdown.room_charges_total || 0).toLocaleString()}</span>
                  </div>
                  {pricingBreakdown.gst_mode !== 'none' && pricingBreakdown.room_gst_total > 0 && (
                    <div className="flex justify-between text-gray-800" data-testid="breakdown-tax-total">
                      <span>{pricingBreakdown.gst_mode === 'including' ? 'Tax component' : 'Tax total'}</span>
                      <span>₹{Number(pricingBreakdown.room_gst_total).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-gray-200 font-bold text-[#1B2A7B]" data-testid="breakdown-grand-total">
                    <span>{pricingBreakdown.gst_mode === 'none' ? 'Total' : 'Grand Total'} for {pricingBreakdown.nights || 0} night{(pricingBreakdown.nights || 0) === 1 ? '' : 's'}</span>
                    <span>₹{Number(pricingBreakdown.grand_total || 0).toLocaleString()}</span>
                  </div>
                  {manualOverride && (
                    <div className="text-amber-700 mt-1 pt-1 border-t border-amber-100">
                      Suggested total: ₹{Number(pricingBreakdown.grand_total || 0).toLocaleString()} —
                      <button
                        type="button"
                        onClick={() => {
                          setManualOverride(false);
                          handleChange('total_amount', String(pricingBreakdown.grand_total || 0));
                        }}
                        className="ml-1 text-[#1B2A7B] hover:underline font-medium"
                        data-testid="reset-to-auto-btn"
                      >
                        Reset to auto
                      </button>
                    </div>
                  )}
                </div>
              )}
              {pricingLoading && (
                <p className="text-xs text-gray-400 mt-1">Calculating pricing…</p>
              )}
              {pricingError && (
                <p className="text-xs text-amber-600 mt-1" data-testid="pricing-error">{pricingError}</p>
              )}
            </div>

            {/* Advance Amount — hide-able via panel walk-in-form-config */}
            {fieldShown('advance_amount') && (
              <div data-testid="field-advance-amount">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Advance Amount{fieldRequired('advance_amount') ? ' *' : ''}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <input
                    type="number"
                    value={formData.advance_amount}
                    onChange={(e) => handleChange('advance_amount', e.target.value)}
                    placeholder="Enter advance received"
                    min="0"
                    className={`w-full pl-10 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.advance_amount ? 'border-red-400' : 'border-gray-200'} text-gray-900 placeholder-gray-400 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                    data-testid="advance-amount-input"
                  />
                </div>
                {errors.advance_amount && (
                  <p className="text-red-500 text-xs mt-1">{errors.advance_amount}</p>
                )}
              </div>
            )}

            {/* Pending Amount - Auto Calculated (only meaningful when advance is shown) */}
            {fieldShown('advance_amount') && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-orange-700">Pending Amount</span>
                  <span className="text-lg font-bold text-orange-700">
                    ₹{pendingAmount > 0 ? pendingAmount.toLocaleString() : 0}
                  </span>
                </div>
                <p className="text-xs text-orange-600 mt-1">Auto-calculated: Total - Advance</p>
              </div>
            )}
          </div>
        </div>

        {/* Dates Section - Updated */}
        {/* iter192 — Booking Dates section relocated above Rooms.
             The old placement here has been removed. */}

        {/* WhatsApp Notification Section — hide-able via panel walk-in-form-config */}
        {fieldShown('whatsapp_number') && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="field-whatsapp-number">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#1B2A7B]" />
            WhatsApp Notification{fieldRequired('whatsapp_number') ? ' *' : ''}
          </h3>
          
          <div className="space-y-4">
            {/* Send WhatsApp checkbox */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.send_whatsapp}
                onChange={(e) => handleChange('send_whatsapp', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-[#1B2A7B] focus:ring-emerald"
                data-testid="send-whatsapp-checkbox"
              />
              <span className="text-sm text-gray-700">Send WhatsApp Notification</span>
            </label>

            {/* Same as Mobile Number checkbox — only relevant when sending */}
            {formData.send_whatsapp && (
              <label className="flex items-center gap-3 cursor-pointer pl-1">
                <input
                  type="checkbox"
                  checked={formData.whatsapp_same_as_mobile}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      whatsapp_same_as_mobile: checked,
                      whatsapp_number: checked
                        ? (prev.mobile_number || '').replace(/\D/g, '').slice(0, 10)
                        : prev.whatsapp_number,
                    }));
                    if (errors.whatsapp_number) {
                      setErrors(prev => ({ ...prev, whatsapp_number: null }));
                    }
                  }}
                  className="w-5 h-5 rounded border-gray-300 text-[#1B2A7B] focus:ring-emerald"
                  data-testid="whatsapp-same-as-mobile-checkbox"
                />
                <span className="text-sm text-gray-700">Same as Mobile Number</span>
              </label>
            )}

            {/* iter278 — Send Menu PDF toggle. New, additive, optional.
                Does NOT modify the two existing toggles above. */}
            {formData.send_whatsapp && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.send_menu}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, send_menu: e.target.checked }))
                  }
                  className="w-5 h-5 rounded border-gray-300 text-[#1B2A7B] focus:ring-emerald"
                  data-testid="walkin-send-menu-checkbox"
                />
                <span className="text-sm text-gray-700">Send Menu PDF</span>
              </label>
            )}

            {/* WhatsApp Number Input */}
            {formData.send_whatsapp && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  WhatsApp Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <div className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-500">+91</div>
                  <input
                    type="tel"
                    value={formData.whatsapp_number}
                    onChange={(e) => handleChange('whatsapp_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10 digit WhatsApp number"
                    maxLength={10}
                    readOnly={formData.whatsapp_same_as_mobile}
                    className={`w-full pl-[4.5rem] pr-4 py-3 ${formData.whatsapp_same_as_mobile ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'} rounded-lg border ${errors.whatsapp_number ? 'border-red-400' : 'border-gray-200'} text-gray-900 placeholder-gray-400 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                    data-testid="whatsapp-number-input"
                  />
                </div>
                {errors.whatsapp_number && (
                  <p className="text-red-500 text-xs mt-1">{errors.whatsapp_number}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {formData.whatsapp_same_as_mobile
                    ? 'Auto-synced with mobile number above'
                    : 'Booking confirmation will be sent via WhatsApp'}
                </p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors shadow-[#1B2A7B]/30"
            data-testid="create-booking-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Booking...
              </>
            ) : (
              'Create Booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingCreateScreen;
